import React from "react";
import { useForm } from "react-hook-form";
import {
    createFailure,
    createValidationResult,
    FailureSeverity,
    Form,
    FormActions,
    FormAdditionalValidation,
    FormDropdown,
    FormFields,
    FormTextField,
    mapToDropdownOption,
    validationFailed,
    ValidationResult,
    validationSuccessful
} from "sprint-common-web";

import {
    LANGUAGE_SPECIFICATION_TOKEN,
    subtitlesTargetFileNameTokens
} from "../../../../../common/utils/tokens/subtitlesTargetFileNameTokens";
import { dynamicSubtitleTypeDropdownOptions } from "../../../../../common/types/DynamicSubtitleType";
import { validateSubtitlesTargetFileName } from "../../../../../common/utils/validateSubtitlesTargetFileName";
import { orderAudioProxyTypeDropdownOptions } from "../../../../../orders/common/types/OrderAudioProxyType";
import { useEditEncodeConfigurationForm } from "./useEditEncodeConfigurationForm";

export interface EditEncodeConfigurationFormInputs {
    muxSpecificationId: string;
    subtitlesTargetFileName: string;
    audioProxyType: string;
    muxSubtitleType?: string;
    copySubtitleType?: string;
    closedCaptionCode?: string;
    subtitlesCaptionCode?: string;
    languageSpecificationId: string;
}

interface EditEncodeConfigurationFormProps {
    systemId: string;
    onCancel(): void;
    onSubmit(): void;
}

export const EditEncodeConfigurationForm: React.VFC<EditEncodeConfigurationFormProps> = ({
    systemId,
    onSubmit,
    onCancel
}) => {
    const {
        itemToEdit,
        muxSpecifications,
        languageSpecifications,
        editEncodeConfiguration
    } = useEditEncodeConfigurationForm(systemId);

    const { errors, control, formState, handleSubmit, getValues } = useForm<EditEncodeConfigurationFormInputs>({
        defaultValues: {
            muxSpecificationId: itemToEdit.muxSpecification?.id,
            subtitlesTargetFileName: itemToEdit.subtitlesTargetFileName,
            audioProxyType: itemToEdit.audioProxyType ? String(itemToEdit.audioProxyType) : undefined,
            muxSubtitleType: itemToEdit.muxSubtitleType ? String(itemToEdit.muxSubtitleType) : undefined,
            copySubtitleType: itemToEdit.copySubtitleType ? String(itemToEdit.copySubtitleType) : undefined,
            languageSpecificationId: itemToEdit.languageSpecification?.id,
            closedCaptionCode: itemToEdit.closedCaptionCode,
            subtitlesCaptionCode: itemToEdit.subtitlesCaptionCode
        },
        criteriaMode: "all"
    });

    const handleEditEncodeConfiguration = async (inputs: EditEncodeConfigurationFormInputs) => {
        await editEncodeConfiguration(inputs);
        onSubmit();
    };

    const handleCancelButtonClick = () => {
        onCancel();
    };

    return (
        <Form onSubmit={handleSubmit(handleEditEncodeConfiguration)}>
            <FormFields>
                <FormDropdown
                    options={muxSpecifications.map((spec) => mapToDropdownOption(spec))}
                    label="Mux specification"
                    name="muxSpecificationId"
                    control={control}
                    errors={errors}
                    rules={{ required: "Select mux specification" }}
                />
                <FormDropdown
                    options={orderAudioProxyTypeDropdownOptions}
                    label="Order audio proxy type"
                    name="audioProxyType"
                    control={control}
                    errors={errors}
                    rules={{ required: "Select audio proxy type" }}
                />
                <FormDropdown
                    options={dynamicSubtitleTypeDropdownOptions}
                    label="Mux subtitle type"
                    name="muxSubtitleType"
                    control={control}
                    errors={errors}
                />
                <FormDropdown
                    options={dynamicSubtitleTypeDropdownOptions}
                    label="Sidecar subtitle type"
                    name="copySubtitleType"
                    control={control}
                    errors={errors}
                />
                <FormTextField
                    label="Subtitles target file name"
                    name="subtitlesTargetFileName"
                    control={control}
                    errors={errors}
                    rules={{
                        required: "Enter subtitles target file name",
                        validate: validateSubtitlesTargetFileName
                    }}
                    tooltipContent={`Available tokens: ${subtitlesTargetFileNameTokens.join(", ")}`}
                />
                <FormTextField label="Closed caption code" name="closedCaptionCode" control={control} errors={errors} />
                <FormTextField
                    label="Subtitles caption code"
                    name="subtitlesCaptionCode"
                    control={control}
                    errors={errors}
                />
                <FormDropdown
                    options={languageSpecifications.map((item) => mapToDropdownOption(item))}
                    label="Language specification"
                    name="languageSpecificationId"
                    control={control}
                    errors={errors}
                    rules={{ validate: () => validateIsLanguageSpecEmpty(getValues()) }}
                />
                <FormAdditionalValidation
                    control={control}
                    name="additionalValidation"
                    errors={errors}
                    rules={{ validate: () => validateAreSubtitleTypesEmpty(getValues()) }}
                />
            </FormFields>
            <FormActions
                isLoading={formState.isValidating || formState.isSubmitting}
                onCancel={handleCancelButtonClick}
            />
        </Form>
    );
};

const validateIsLanguageSpecEmpty = ({
    subtitlesTargetFileName,
    languageSpecificationId
}: EditEncodeConfigurationFormInputs): ValidationResult => {
    if (
        subtitlesTargetFileName &&
        subtitlesTargetFileName.includes(LANGUAGE_SPECIFICATION_TOKEN) &&
        !languageSpecificationId
    ) {
        const failure = createFailure(
            "Language specification is empty",
            "languageSpecification",
            FailureSeverity.Error
        );

        return validationFailed([failure]);
    }

    return validationSuccessful();
};

const validateAreSubtitleTypesEmpty = ({
    copySubtitleType,
    muxSubtitleType
}: EditEncodeConfigurationFormInputs): ValidationResult => {
    if (!copySubtitleType && !muxSubtitleType) {
        const failure = createFailure(
            `Mux subtitle type or sidecar subtitle type must be provided`,
            "no-subtitle-type",
            FailureSeverity.Error
        );

        return createValidationResult([failure]);
    }

    return validationSuccessful();
};
